import { Presenter } from '@d-id/types';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Alert, Box, Card, CircularProgress, IconButton, Snackbar, Typography, styled } from '@mui/material';
import React, { ReactNode, useCallback } from 'react';
import { DraggableProvided, DraggableStateSnapshot } from 'react-beautiful-dnd';

export const AvatarList = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    maxHeight: '600px',
    overflowY: 'auto',
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
}));

export const AvatarCard = styled(Card, {
    shouldForwardProp: prop => prop !== 'isDragging',
})<{ isDragging?: boolean }>(({ theme, isDragging }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.5),
    cursor: 'default',
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging ? theme.palette.primary.light : 'transparent',
    transform: isDragging ? 'scale(1.02)' : 'none',
    transition: 'all 0.2s ease',
    minHeight: '80px',
    position: 'relative',
    userSelect: 'none',
    '&:hover': {
        boxShadow: theme.shadows[4],
        backgroundColor: theme.palette.grey[50],
    },
}));

export const AvatarImage = styled('img')<{ width?: string; height?: string }>(({ theme, width = '60px', height = '60px' }) => ({
    width,
    height,
    objectFit: 'cover',
    borderRadius: theme.spacing(1),
    marginRight: theme.spacing(2),
    flexShrink: 0,
}));

export const AvatarInfo = styled(Box)({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    minWidth: 0,
});

export const DragHandle = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(1),
    color: theme.palette.text.secondary,
    cursor: 'grab',
    userSelect: 'none',
    '&:hover': {
        color: theme.palette.primary.main,
    },
    '&:active': {
        cursor: 'grabbing',
    },
}));

export const OutputContainer = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(1),
    position: 'relative',
}));

export const CopyButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
}));

export const JsonOutput = styled('pre')(({ theme }) => ({
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(2),
    borderRadius: theme.spacing(0.5),
    border: `1px solid ${theme.palette.divider}`,
    fontSize: '12px',
    fontFamily: 'monospace',
    maxHeight: '200px',
    overflowY: 'auto',
    margin: 0,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
}));

export const FilterContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    alignItems: 'center',
}));

export const GroupContainer = styled(Box)(({ theme }) => ({
    marginLeft: theme.spacing(4),
    paddingLeft: theme.spacing(2),
    borderLeft: `2px solid ${theme.palette.primary.light}`,
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(1),
}));

export const DropZone = styled(Box)(({ theme }) => ({
    minHeight: '60px',
    border: `2px dashed ${theme.palette.grey[300]}`,
    borderRadius: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.grey[50],
    margin: theme.spacing(1, 0),
    transition: 'all 0.2s ease',
    '&.drag-over': {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.light,
    },
}));

interface LoadingStateProps {
    isLoading: boolean;
    message?: string;
}

export const LoadingState: React.FC<LoadingStateProps> = ({ isLoading, message = 'Loading avatars...' }) => {
    if (!isLoading) return null;

    return (
        <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={4}>
            <CircularProgress />
            <Typography>{message}</Typography>
        </Box>
    );
};

interface EmptyStateProps {
    message: string;
    severity?: 'warning' | 'error' | 'info';
}

export const EmptyState: React.FC<EmptyStateProps> = ({ message, severity = 'warning' }) => (
    <Box>
        <Alert severity={severity}>
            <Typography variant="body2">
                <strong>{message}</strong>
            </Typography>
        </Alert>
    </Box>
);

interface JsonOutputSectionProps {
    title: string;
    jsonContent: string;
    onCopy: () => void;
    linkUrl?: string;
}

export const JsonOutputSection: React.FC<JsonOutputSectionProps> = ({ title, jsonContent, onCopy, linkUrl }) => (
    <OutputContainer>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            {linkUrl ? (
                <Typography
                    component="a"
                    href={linkUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                        color: 'primary.main',
                        textDecoration: 'underline',
                        fontWeight: 600,
                        fontSize: 'inherit',
                        '&:hover': { textDecoration: 'none' },
                    }}>
                    {title}
                </Typography>
            ) : (
                title
            )}
        </Typography>
        <CopyButton onClick={onCopy} size="small" title="Copy to clipboard">
            <ContentCopyIcon fontSize="small" />
        </CopyButton>
        <JsonOutput>{jsonContent}</JsonOutput>
    </OutputContainer>
);

interface CopySnackbarProps {
    open: boolean;
    onClose: () => void;
    message?: string;
}

export const CopySnackbar: React.FC<CopySnackbarProps> = ({ open, onClose, message = 'JSON copied to clipboard!' }) => (
    <Snackbar open={open} autoHideDuration={3000} onClose={onClose} message={message} />
);

interface BasicAvatarCardProps {
    avatar: Presenter;
    index: number;
    provided: DraggableProvided;
    snapshot: DraggableStateSnapshot;
    children?: ReactNode;
}

export const BasicAvatarCard: React.FC<BasicAvatarCardProps> = ({ avatar, index, provided, snapshot, children }) => (
    <AvatarCard ref={provided.innerRef} {...provided.draggableProps} isDragging={snapshot.isDragging}>
        <DragHandle {...provided.dragHandleProps}>
            <DragIndicatorIcon />
        </DragHandle>
        <AvatarImage src={avatar.thumbnail || avatar.poster} alt={avatar.name} />
        <AvatarInfo>
            <Typography
                variant="body2"
                sx={{
                    fontWeight: 600,
                    mb: 0.3,
                    lineHeight: 1.2,
                    fontSize: '1.2rem',
                }}>
                #{index + 1} - {avatar.name}
            </Typography>
            <Typography
                variant="body2"
                sx={{
                    color: 'text.secondary',
                    fontFamily: 'monospace',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    fontSize: '1rem',
                }}>
                {avatar.id}
            </Typography>
        </AvatarInfo>
        {children}
    </AvatarCard>
);

// Common drag handlers
export const createDragHandlers = () => {
    const handleDragStart = useCallback(() => {
        if (typeof window !== 'undefined') {
            document.body.style.overflow = 'hidden';
        }
    }, []);

    const handleDragEndCleanup = useCallback(() => {
        if (typeof window !== 'undefined') {
            document.body.style.overflow = 'unset';
        }
    }, []);

    React.useEffect(() => {
        return () => {
            if (typeof window !== 'undefined') {
                document.body.style.overflow = 'unset';
            }
        };
    }, []);

    return { handleDragStart, handleDragEndCleanup };
};

export const createClipboardHandler = (generateOutput: () => string, setShowSuccess: (show: boolean) => void) => {
    return useCallback(async () => {
        try {
            await navigator.clipboard.writeText(generateOutput());
            setShowSuccess(true);
        } catch (err) {
            console.error('Failed to copy: ', err);
        }
    }, [generateOutput, setShowSuccess]);
};
